import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleXmark, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import AmpInlineLoadingSpinner from "../AmpInlineLoadingSpinner/AmpInlineLoadingSpinner";
import ValidateResultIcon from "../ValidateResultIcon/ValidateResultIcon";

import "./AmpInput.css";
import { useDispatch } from "react-redux";
import { AUTOCOMPLETE } from "../../../redux/actionTypes/actionsTypes";
import { AutoCompleteItem } from "../../../redux/reducers/getNBHDReducer/getAutoCompleteReducer";

interface AmpInputProps {
    name: string,
    id: string,
    type: "text" | "password" | "number" | "hidden"
    className?: string,
    placeholder?: string,
    ariaLabel?: string,
    label?: string,
    disabled?: boolean,
    onInput?: (e: string) => void;
    onChange?: React.ChangeEventHandler<HTMLInputElement>;
    onPaste?: React.ClipboardEventHandler;
    noTimer?: boolean;
    loading?: boolean;
    value?: string;
    isError?: boolean;
    isValid?: boolean;
    autoComplete?: string;
    errorMessage?: string;
    maxLength?: number;
    min?: number;
    max?: number;
    testid?: string;
    readOnly?: boolean;
    autoCompleteAPIEnabled?: boolean;
    autoCompleteAPIItems?: Array<AutoCompleteItem>;
    onAutoCompleteSelect?: (item: AutoCompleteItem) => void;
    realtiveAutoCompleteResultsPosition?: boolean
}

export default function AmpInput(props: AmpInputProps): JSX.Element {

    const [val, setVal] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const dispatch = useDispatch();

    const [hideAutoComplete, setHide] = useState(false);

    useEffect(() => {
        if (props.value !== undefined) {
            setVal(props.value);
        }
    }, [props.value]);

    useEffect(() => {

        const handleInput = (e: string) => {
            if (typeof props.onInput === "function") {
                props.onInput(e);
            }
            return;
        }

        if (props.noTimer) {
            handleInput(val);
            return;
        } else {
            const timeOutId = setTimeout(() => handleInput(val), 1000);
            return () => clearTimeout(timeOutId)
        }

    }, [val, props]);

    const autoCompleteItem = (value: AutoCompleteItem) => {
        return (
            <div onClick={() => {
                setVal(value.address);
                dispatch({ type: AUTOCOMPLETE.RESET });
                if (props.onAutoCompleteSelect) {
                    props.onAutoCompleteSelect(value);
                }
            }}><strong>{`${value.address} - ${value.city}`}</strong></div>
        )
    };

    const changeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        if(props.onChange){
            props.onChange(e);
            setHide(false);
            setVal((e.target as HTMLInputElement).value);
        } else {
            setVal((e.target as HTMLInputElement).value);
        }
    }

    const focusOut = () => {
        setTimeout(() => {
            setHide(true);
        }, 500);
    }

    return (
        <>
            <input
                title={props.label}
                readOnly={props.readOnly}
                value={val}
                className={`amp-input${props.type === "password" ? " amp-password-input" : ""}${props.className ? " " + props.className : ""}${props.isError ? " error" : ""}`}
                onChange={(e) => {changeHandler(e)}                }
                type={props.type === "password" ? (showPassword ? "text" : "password") : props.type}
                id={props.id}
                name={props.name} placeholder={props.placeholder}
                disabled={props.disabled}
                autoComplete={props.autoComplete}
                aria-label={props.ariaLabel}
                data-testid={props.testid ?? "input-test"}
                onPaste={props.onPaste}
                maxLength={props.maxLength}
                min={props.min}
                max={props.max}
                onBlur={() => focusOut()}>
                
            </input>
            {
                props.autoCompleteAPIEnabled && (
                    <div className={`autocomplete-items ${props.realtiveAutoCompleteResultsPosition && "relative"} ${hideAutoComplete && "hidden"}`}>
                        {!props.realtiveAutoCompleteResultsPosition && (
                            <>
                                {props.autoCompleteAPIItems?.map(
                                    (value) => {
                                        return autoCompleteItem(value);
                                    })
                                }
                            </>
                        )}
                        {props.realtiveAutoCompleteResultsPosition && (
                            <div className="relative-items">
                                {props.autoCompleteAPIItems?.map(
                                    (value) => {
                                        return autoCompleteItem(value);
                                    })
                                }
                            </div>
                        )}

                    </div>
                )
            }

            {props.type === "password" && <button type="button" className="show-password-button" onClick={() => setShowPassword(!showPassword)} aria-label="show-password-button"><FontAwesomeIcon icon={showPassword ? faEye : faEyeSlash} /></button>}
            <ValidateResultIcon isValid={props.isValid} />
            {props.loading ? <AmpInlineLoadingSpinner /> : ""}
            {props.errorMessage ? <div className="input-error-message"><FontAwesomeIcon className="red-cross" icon={faCircleXmark} /> {props.errorMessage}</div> : ""}
        </>
    )
}